import {
  ResolvedCUEElement,
  ResolvedProductListing,
} from "@app/layouts/Article/elements";
import amazonPricingDataLoader, {
  extractAmazonLoader,
} from "@app/utils/amazonPricingDataloader";
import { configSingleStory } from "@pub/config";
import { Arrow, ProductContent } from "@sphtech/dmg-design-system";
import { Fragment, ReactNode, useState } from "react";
import useSWR from "swr";

import styles from "./product.module.css";
import productDataDataLoader from "./productDataLoader";
import { maybeMergeAmazonData } from "./utils";

type Props = {
  element: ResolvedProductListing;
  allElements: ResolvedCUEElement[];
};

export default function ProductIndex({
  element,
  allElements,
}: Props): ReactNode {
  const [showMore, setShowMore] = useState(false);
  const filterProduct = allElements
    .filter((item) => item.relation?.type === "product")
    .map((item) => item.relation?.id);

  const { data, isLoading } = useSWR(filterProduct, (keys) =>
    productDataDataLoader.loadMany(keys).then((data) => {
      return data.map((data) => (data instanceof Error ? null : data));
    }),
  );

  const asins = data
    ?.flatMap((item) => item?.dealsSource.map((deal) => deal.dynamicPricing))
    .filter((id) => id);

  const { data: extractedAmazonData } = useSWR(asins, () => {
    const response =
      asins &&
      amazonPricingDataLoader.loadMany(asins).then(extractAmazonLoader);
    return response;
  });

  const itemsPerPage = element.additionalFields.items_per_page?.value || false;
  const hasMoreItemsToShow =
    itemsPerPage && data?.length && data.length > itemsPerPage;

  const showMoreHandler = () => {
    setShowMore(!showMore);
  };

  return (
    <Fragment>
      {!isLoading && (
        <div className={styles.bodyContainer}>
          <div className={styles.indexContainer}>
            <h2 className={styles.indexTitle}>
              {element.additionalFields.title?.value}
            </h2>
            <div className={styles.indexItemContainer}>
              {data?.map((item, index) => {
                if (itemsPerPage && index > itemsPerPage - 1 && !showMore) {
                  return null;
                }

                return (
                  <div className={styles.indexItem} key={index}>
                    {item && (
                      <ProductContent
                        {...item}
                        cons={undefined}
                        pros={undefined}
                        discount=""
                        dealsSource={maybeMergeAmazonData(
                          item.dealsSource,
                          extractedAmazonData,
                        )}
                        hidePriceInButton={
                          configSingleStory.productContent?.hidePriceInButton
                        }
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {hasMoreItemsToShow && (
              <button
                onClick={() => {
                  showMoreHandler();
                }}
                style={{ height: "54px" }}
              >
                {showMore ? "SHOW LESS" : "SHOW MORE"}
                <Arrow
                  direction={showMore ? "up" : "down"}
                  size={16.71}
                  strokeWidth={1}
                />
              </button>
            )}
          </div>
        </div>
      )}
    </Fragment>
  );
}

export { productDataDataLoader };
